// @ts-nocheck

import {
    SELLYO_FACEBOOK_PIXEL_ID,
    SELLYO_DEFAULT_DOMAIN,
    PIXEL_EVENTS,
    PIXEL_SCRIPT_TIMEOUT,
    PIXEL_RETRY_CONFIG,
    EVENT_ID_STORAGE_PREFIX,
} from './pixelConstants';

/**
 * Garante que o pixel da Sellyo sempre está incluído na lista de pixels
 * O pixel da Sellyo é usado para analytics interno e backup de tracking
 * @param pixels Lista de pixels configurados pelo usuário (pode estar vazia)
 * @returns Lista de pixels com o pixel da Sellyo sempre incluído
 */
export const ensureSellyoPixel = (pixels: Pixel[] = []): Pixel[] => {
    // Verificar se já existe um pixel da Sellyo na lista
    const hasSellyoPixel = pixels.some(
        p => p.provider === 'FACEBOOK' && p.pixelId === SELLYO_FACEBOOK_PIXEL_ID
    );

    // Se já existe, retornar lista original
    if (hasSellyoPixel) {
        return pixels;
    }

    // Adicionar pixel da Sellyo no início da lista (será inicializado primeiro)
    const sellyoPixel: Pixel = {
        id: 'sellyo-internal',
        provider: 'FACEBOOK',
        pixelId: SELLYO_FACEBOOK_PIXEL_ID,
    };

    return [sellyoPixel, ...pixels];
};

/**
 * Inicializa ambos os pixels (seller + Sellyo) na ordem correta
 * Garante que o init seja idempotente (não duplica se já foi chamado)
 * @param pixels Lista de pixels do seller
 * @returns Promise que resolve quando todos os pixels estão inicializados
 */
const initPixels = async (pixels: Pixel[]): Promise<void> => {
    // Garantir que o pixel da Sellyo está incluído
    const allPixels = ensureSellyoPixel(pixels);
    
    // Agrupar pixels por provider para otimizar carregamento
    const pixelsByProvider = allPixels.reduce((acc, pixel) => {
        const providerKey = pixel.provider.toLowerCase();
        if (!acc[providerKey]) acc[providerKey] = [];
        acc[providerKey].push(pixel);
        return acc;
    }, {} as Record<string, Pixel[]>);

    // Inicializar pixels agrupados
    for (const [providerKey, providerPixels] of Object.entries(pixelsByProvider)) {
        const provider = PROVIDERS[providerKey];
        if (!provider) {
            console.warn(`[Pixel] Provider ${providerKey} não suportado`);
            continue;
        }

        for (const pixel of providerPixels) {
            // Monta src dinâmico se for função
            const src =
                typeof provider.src === "function"
                    ? provider.src(pixel)
                    : provider.src.replace("__DYNAMIC__", pixel.pixelId ?? "");

            try {
                // Facebook: o snippet oficial (no init) cria o fbq e carrega o fbevents.js uma
                // vez só, enfileirando os eventos até o script chegar. Injetar o fbevents.js
                // antes do fbq existir dava "fbq is not defined" e carregava o script 3 vezes.
                if (providerKey === 'facebook') {
                    provider.init(pixel);
                    continue;
                }
                // Usar ID único para cada pixel para evitar conflitos
                const scriptId = `${provider.scriptId}-${pixel.pixelId || pixel.id}`;
                await injectScript(scriptId, src);
                provider.init(pixel);
            } catch (err) {
                console.error(`[Pixel] Falha ao injetar ${pixel.provider} (${pixel.pixelId}):`, err);
            }
        }
    }
};

/**
 * Dispara o mesmo evento para ambos os pixels (seller + Sellyo)
 * Garante que os parâmetros obrigatórios do Meta estão presentes
 * @param pixels Lista de pixels do seller
 * @param event Nome do evento (PageView, ViewContent, InitiateCheckout, Purchase)
 * @param payload Dados do evento
 */
const trackBoth = (pixels: Pixel[], event: string, payload?: EventPayload, options?: FireOptions): void => {
    // Garantir que o pixel da Sellyo está incluído (exceto evento só do seller, ex.: boleto gerado)
    const allPixels = options?.sellerOnly ? pixels : ensureSellyoPixel(pixels);
    
    // Validar parâmetros obrigatórios do Meta Pixel
    const validatedPayload = { ...payload };
    
    // Para eventos que requerem parâmetros específicos
    if (['ViewContent', 'InitiateCheckout', 'AddPaymentInfo', 'Purchase'].includes(event)) {
        // Garantir content_ids (obrigatório)
        if (!validatedPayload.content_ids || !Array.isArray(validatedPayload.content_ids)) {
            validatedPayload.content_ids = ['unknown'];
        }
        
        // Garantir content_type (obrigatório)
        if (!validatedPayload.content_type) {
            validatedPayload.content_type = 'product';
        }
        
        // Garantir value (obrigatório para eventos de conversão)
        if (validatedPayload.value === undefined || validatedPayload.value === null) {
            validatedPayload.value = 0;
        }
        
        // Garantir currency (obrigatório)
        if (!validatedPayload.currency) {
            validatedPayload.currency = 'BRL';
        }
    }
    
    // Disparar para todos os pixels
    for (const pixel of allPixels) {
        // Validar se o pixel está ativo
        if (pixel.status && pixel.status !== 'active') {
            continue;
        }

        // Eventos escolhidos no pixel (pixel sem `events` = todos, como sempre foi)
        if (event === 'InitiateCheckout' && pixel.events?.initiateCheckout === false) continue;
        if (event === 'Purchase' && pixel.events?.purchase === false) continue;

        const providerKey = pixel.provider.toLowerCase();
        const provider = PROVIDERS[providerKey];
        
        if (!provider) {
            console.warn(`[Pixel] Provider ${providerKey} não encontrado`);
            continue;
        }

        try {
            // Aplicar porcentagem de valor para PIX/Boleto se configurado
            const adjustedPayload = { ...validatedPayload };
            
            if (event === 'Purchase' && validatedPayload?.value) {
                const paymentMethod = validatedPayload.paymentMethod || validatedPayload.method;
                
                // `!= null`: 0% é configuração válida (antes caía como "sem %" e ia o valor cheio)
                if (paymentMethod === 'pix' && pixel.pixPercentage != null) {
                    adjustedPayload.value = (Number(validatedPayload.value) * pixel.pixPercentage) / 100;
                } else if (paymentMethod === 'boleto' && pixel.boletoPercentage != null) {
                    adjustedPayload.value = (Number(validatedPayload.value) * pixel.boletoPercentage) / 100;
                }
            }

            provider.fireEvent(pixel, event, adjustedPayload);
        } catch (err) {
            console.error(`[Pixel] Erro ao disparar evento ${event} para ${pixel.provider} (${pixel.pixelId}):`, err);
        }
    }
};

/**
 * Gera ou recupera um event_id determinístico para dedupe com CAPI
 * @param kind Tipo do evento ('pageview', 'viewcontent', 'initcheckout', 'addpaymentinfo', 'purchase')
 * @param key Chave única (orderId, sessionId, cartId, etc). Se não fornecida, gera UUID
 * @returns event_id único e determinístico
 */
/**
 * Parte do event_id que vem da chave. O backend (API de Conversões) só aceita
 * [A-Za-z0-9_-] e até 100 caracteres. O sessionId do checkout tem "?key=", "=" e
 * passa disso, então o InitiateCheckout era recusado (400) e não chegava à Meta.
 * Chave já no formato (ex.: orderId UUID do Purchase) segue igual; as outras viram
 * um hash fixo, o mesmo no navegador e no servidor, então a deduplicação continua.
 */
const eventIdKey = (key: string): string => {
    if (/^[\w-]{1,80}$/.test(key)) return key;
    // cyrb53 duas vezes (sementes diferentes) = ~106 bits, sem colisão na prática
    const cyrb53 = (str: string, seed: number) => {
        let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
        for (let i = 0; i < str.length; i++) {
            const ch = str.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
    };
    return `s${cyrb53(key, 1)}${cyrb53(key, 2)}`;
};

export const getOrCreateEventId = (
    kind: 'pageview' | 'viewcontent' | 'initcheckout' | 'addpaymentinfo' | 'purchase',
    key?: string
): string => {
    // Se temos uma chave (orderId, sessionId), criar event_id determinístico
    if (key) {
        const eventId = `${kind}_${eventIdKey(key)}`;
        const storageKey = `${EVENT_ID_STORAGE_PREFIX}${kind}:${key}`;
        
        try {
            // Tentar recuperar do sessionStorage primeiro
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                return stored;
            }
            
            // Armazenar para futuras referências
            sessionStorage.setItem(storageKey, eventId);
            return eventId;
        } catch (err) {
            console.warn('[EventID] Erro ao acessar sessionStorage:', err);
            return eventId;
        }
    }
    
    // Sem chave, gerar UUID v4
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    
    const eventId = `${kind}_${uuid}`;
    const storageKey = `${EVENT_ID_STORAGE_PREFIX}${kind}:${uuid}`;
    
    try {
        sessionStorage.setItem(storageKey, eventId);
    } catch (err) {
        console.warn('[EventID] Erro ao armazenar event_id:', err);
    }
    
    return eventId;
};

export { };

declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
        _fbq?: any;
        gtag?: (...args: any[]) => void;
        dataLayer?: any[];
        ttq?: any;
        TiktokAnalyticsObject?: any;
        _sellyoPixelInitialized?: boolean;
    }
}

type Pixel = {
    id: string;
    provider: string; // FACEBOOK, GOOGLE, TIKTOK, TABOOLA, OUTBRAIN, PINTEREST, etc
    pixelId?: string;
    accessToken?: string;
    conversionId?: string;
    conversionLabel?: string;
    domain?: string; // Domínio personalizado para disparo do pixel
    pixPercentage?: number; // Porcentagem do valor para PIX
    boletoPercentage?: number; // Porcentagem do valor para Boleto
    enableBoletoEvent?: boolean; // Se deve disparar Purchase para boleto
    trackingOptions?: {
        creditCard?: boolean;
        pix?: boolean;
        billet?: boolean;
    };
    [key: string]: any; // Permite qualquer campo extra
};

type EventPayload = {
    value?: number | string;
    currency?: string;
    transactionId?: string;
    [key: string]: any;
};

type ProviderInitFunction = (pixel: Pixel) => Promise<void> | void;
type ProviderEventFunction = (pixel: Pixel, event: string, payload?: EventPayload) => void;

type ProviderDefinition = {
    scriptId: string;
    src: string | ((pixel: Pixel) => string);
    init: ProviderInitFunction;
    fireEvent: ProviderEventFunction;
};

// ─── Advanced Matching (dados do comprador para o pixel no navegador) ───────────
// O pixel faz o hash sozinho; aqui só normalizamos no formato que a Meta casa.
let advancedMatching: Record<string, string> = {};
const currentAdvancedMatching = () => ({ ...advancedMatching });

const normPhoneBR = (v?: string) => {
    const d = (v || '').replace(/\D/g, '');
    if (!d) return undefined;
    return (d.length === 10 || d.length === 11) && !d.startsWith('55') ? `55${d}` : d;
};

/**
 * Informa os dados do comprador aos pixels do Facebook já iniciados (ex.: na página de
 * sucesso). Mesmo padrão da API de Conversões: external_id = CPF/CNPJ só dígitos.
 */
export const setAdvancedMatching = (pixels: Pixel[], user: { email?: string; phone?: string; name?: string; document?: string }) => {
    const [first, ...rest] = (user.name || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    const data: Record<string, string | undefined> = {
        em: user.email?.trim().toLowerCase() || undefined,
        ph: normPhoneBR(user.phone),
        fn: first,
        ln: rest.join(' ') || undefined,
        external_id: (user.document || '').replace(/\D/g, '') || undefined,
        country: 'br',
    };
    advancedMatching = Object.fromEntries(Object.entries(data).filter(([, v]) => !!v)) as Record<string, string>;

    if (!(window as any).fbq) return;
    for (const pixel of ensureSellyoPixel(pixels)) {
        if (pixel.provider?.toLowerCase() !== 'facebook' || !pixel.pixelId) continue;
        (window as any).fbq('init', pixel.pixelId, currentAdvancedMatching());
    }
};

// Injetor genérico de scripts com timeout e retry
const injectScript = (id: string, src: string, retries = 0): Promise<void> => {
    if (document.getElementById(id)) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = id;
        script.async = true;
        script.src = src;
        
        // Timeout para evitar travamento
        const timeout = setTimeout(() => {
            script.remove();
            if (retries < PIXEL_RETRY_CONFIG.MAX_RETRIES) {
                console.warn(`[Pixel] Timeout ao carregar ${id}, tentando novamente... (${retries + 1}/${PIXEL_RETRY_CONFIG.MAX_RETRIES})`);
                const delay = PIXEL_RETRY_CONFIG.RETRY_DELAY * Math.pow(PIXEL_RETRY_CONFIG.BACKOFF_MULTIPLIER, retries);
                setTimeout(() => {
                    injectScript(id, src, retries + 1).then(resolve).catch(reject);
                }, delay);
            } else {
                reject(new Error(`Timeout ao carregar script ${id}`));
            }
        }, PIXEL_SCRIPT_TIMEOUT);
        
        script.onload = () => {
            clearTimeout(timeout);
            resolve();
        };
        
        script.onerror = (e) => {
            clearTimeout(timeout);
            script.remove();
            if (retries < PIXEL_RETRY_CONFIG.MAX_RETRIES) {
                console.warn(`[Pixel] Erro ao carregar ${id}, tentando novamente... (${retries + 1}/${PIXEL_RETRY_CONFIG.MAX_RETRIES})`);
                const delay = PIXEL_RETRY_CONFIG.RETRY_DELAY * Math.pow(PIXEL_RETRY_CONFIG.BACKOFF_MULTIPLIER, retries);
                setTimeout(() => {
                    injectScript(id, src, retries + 1).then(resolve).catch(reject);
                }, delay);
            } else {
                reject(e);
            }
        };
        
        document.head.appendChild(script);
    });
};

// Definição de provedores
const PROVIDERS: Record<string, ProviderDefinition> = {
    facebook: {
        scriptId: "fbq-script",
        src: "https://connect.facebook.net/en_US/fbevents.js",
        init: (pixel) => {
            try {
                if (!(window as any).fbq) {
                    !(function (f: any, b, e, v, n?, t?, s?) {
                        if (f.fbq) return;
                        n = f.fbq = function () {
                            n.callMethod
                                ? n.callMethod.apply(n, arguments)
                                : n.queue.push(arguments);
                        };
                        if (!f._fbq) f._fbq = n;
                        n.push = n;
                        n.loaded = !0;
                        n.version = "2.0";
                        n.queue = [];
                        t = b.createElement(e);
                        t.async = !0;
                        t.src = v;
                        s = b.getElementsByTagName(e)[0];
                        s.parentNode.insertBefore(t, s);
                    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
                }
                
                // O 3º argumento do init é Advanced Matching (dados do COMPRADOR), que o pixel
                // envia na URL da requisição. Nunca token nem domínio aqui: o token vazava e o
                // domínio virava o mesmo external_id para todos os compradores.
                (window as any).fbq("init", pixel.pixelId, currentAdvancedMatching());
            } catch (error) {
                console.error(`[Pixel] Erro ao inicializar Facebook Pixel ${pixel.pixelId}:`, error);
            }
        },
        fireEvent: (pixel, event, payload) => {
            try {
                if (!(window as any).fbq) {
                    console.warn(`[Pixel] Facebook Pixel não está carregado, evento ${event} não foi disparado`);
                    return;
                }
                
                // event_id vai no 4º argumento: é o ÚNICO lugar que o Facebook lê para deduplicar
                // com a API de Conversões. Dentro do payload ele vira só um parâmetro qualquer.
                const { eventID, paymentMethod, method, transactionId, ...params } = payload || {};
                const options = eventID ? { eventID } : undefined;

                // trackSingle: dispara só para este pixel. Evento fora do padrão Meta (pix,
                // boleto, creditCard) exige a variante Custom.
                const standard = ['PageView', 'ViewContent', 'InitiateCheckout', 'AddPaymentInfo', 'Purchase'].includes(event);
                (window as any).fbq(standard ? 'trackSingle' : 'trackSingleCustom', pixel.pixelId, event, params, options);
            } catch (error) {
                console.error(`[Pixel] Erro ao disparar evento ${event} para Facebook Pixel ${pixel.pixelId}:`, error);
            }
        },
    },
    google: {
        scriptId: "gtag-script",
        src: (pixel) => `https://www.googletagmanager.com/gtag/js?id=${pixel.pixelId}`,
        init: (pixel) => {
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).gtag = (window as any).gtag || function () {
                (window as any).dataLayer.push(arguments);
            };
            (window as any).gtag("js", new Date());
            (window as any).gtag("config", pixel.pixelId);
        },
        fireEvent: (pixel, event, payload) => {
            if (typeof (window as any).gtag === "function") {
                switch (event) {
                    case "InitiateCheckout":
                        (window as any).gtag("event", "begin_checkout", payload);
                        break;
                    case "Purchase":
                        (window as any).gtag("event", "purchase", {
                            transaction_id: payload?.transactionId,
                            value: payload?.value,
                            currency: payload?.currency,
                            ...payload,
                        });
                        if (pixel.conversionLabel) {
                            (window as any).gtag("event", "conversion", {
                                send_to: `${pixel.pixelId}/${pixel.conversionLabel}`,
                                value: payload?.value,
                                currency: payload?.currency,
                                transaction_id: payload?.transactionId,
                            });
                        }
                        break;
                    default:
                        (window as any).gtag("event", event, payload);
                        break;
                }
            }
        },
    },
    tiktok: {
        scriptId: "ttq-script",
        src: "https://analytics.tiktok.com/i18n/pixel/sdk.js?sdkid=__DYNAMIC__",
        init: (pixel) => {
            try {
                if (!(window as any).ttq) {
                    !(function (w: any, d, t) {
                        w.TiktokAnalyticsObject = t;
                        const ttq = (w[t] = w[t] || []);
                        ttq.methods = [
                            "page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"
                        ];
                        ttq.setAndDefer = function (t, e) {
                            t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
                        };
                        for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
                        ttq.instance = function (t) {
                            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
                            return e;
                        };
                        ttq.load = function (e, n) {
                            const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                            ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = i, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {};
                            const o = d.createElement("script"); o.type = "text/javascript", o.async = !0, o.src = i + "?sdkid=" + e + "&lib=" + t;
                            const a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a)
                        };
                    })(window, document, "ttq");
                }
                (window as any).ttq.load(pixel.pixelId);
            } catch (error) {
                console.error(`[Pixel] Erro ao inicializar TikTok Pixel ${pixel.pixelId}:`, error);
            }
        },
        fireEvent: (pixel, event, payload = {}) => {
            try {
                if (!(window as any).ttq) {
                    console.warn(`[Pixel] TikTok Pixel não está carregado, evento ${event} não foi disparado`);
                    return;
                }
                
                // Garante que content_id está presente no payload
                if (!payload.content_id && !payload.contentId) {
                    payload.content_id = payload.productId || payload.offerId || pixel.pixelId || pixel.id || 'unknown';
                }
                
                switch (event) {
                    case "InitiateCheckout":
                        (window as any).ttq.track("InitiateCheckout", payload);
                        break;
                    case "Purchase":
                        (window as any).ttq.track("CompletePayment", payload);
                        break;
                    default:
                        (window as any).ttq.track(event, payload);
                        break;
                }
            } catch (error) {
                console.error(`[Pixel] Erro ao disparar evento ${event} para TikTok Pixel ${pixel.pixelId}:`, error);
            }
        },
    },
    // Exemplo de expansão:
    // taboola: {
    //   scriptId: 'taboola-script',
    //   src: 'https://cdn.taboola.com/libtrc/your-pixel-id/loader.js',
    //   init: (pixel) => { /*...*/ },
    //   fireEvent: (pixel, event, payload) => { /*...*/ }
    // },
    // outbrain: { ... },
    // pinterest: { ... },
};

/**
 * Inicializa o Pixel da Sellyo para disparar PageView
 * Necessário para que os cookies do Facebook sejam identificados (iOS 14+)
 */
export const initializeSellyoPixel = async (): Promise<void> => {
    if ((window as any)._sellyoPixelInitialized) {
        return;
    }

    try {
        const sellyoPixel: Pixel = {
            id: 'sellyo-pixel',
            provider: 'facebook',
            pixelId: SELLYO_FACEBOOK_PIXEL_ID,
            domain: SELLYO_DEFAULT_DOMAIN,
        };

        // O init cria o fbq e carrega o fbevents.js (mesmo motivo do initPixels)
        PROVIDERS.facebook.init(sellyoPixel);
        
        // Disparar PageView da Sellyo
        if ((window as any).fbq) {
            (window as any).fbq('trackSingle', SELLYO_FACEBOOK_PIXEL_ID, 'PageView');
        }

        (window as any)._sellyoPixelInitialized = true;
    } catch (err) {
        console.error('[Pixel] Falha ao inicializar Pixel da Sellyo:', err);
    }
};

/**
 * Universal loader de pixels com suporte a múltiplos pixels e domínios
 * MANTÉM COMPATIBILIDADE: Função pública existente, agora usa initPixels internamente
 */
export const initializeAllPixels = async (pixels: Pixel[]) => {
    // Usar a nova função initPixels que garante Sellyo + seller na ordem correta
    await initPixels(pixels);
};

/**
 * Dispara evento (InitiateCheckout, Purchase, etc) para todos os pixels
 * com tratamento robusto de erros e validações
 * MANTÉM COMPATIBILIDADE: Função pública existente, agora usa trackBoth internamente
 */
export type FireOptions = {
    /** Não inclui o pixel da Sellyo (ela só conta venda paga) */
    sellerOnly?: boolean;
};

export const firePixelEvent = (pixels: Pixel[], event: string, payload?: EventPayload, options?: FireOptions) => {
    // Usar trackBoth que garante Sellyo + seller com parâmetros validados
    trackBoth(pixels, event, payload, options);
};

/**
 * Atalho para disparar InitiateCheckout
 */
export const fireInitiateCheckout = (pixels: Pixel[], payload?: EventPayload) => {
    firePixelEvent(pixels, "InitiateCheckout", payload);
};

/**
 * Atalho para Purchase com validações específicas
 */
export const firePurchase = (pixels: Pixel[], payload: EventPayload) => {
    firePixelEvent(pixels, "Purchase", payload);
};

/**
 * Dispara evento de Purchase para Cartão de Crédito
 */
export const fireCreditCardPurchase = (pixels: Pixel[], payload: EventPayload) => {
    const activePixels = pixels.filter(p => p.trackingOptions?.creditCard !== false);
    firePixelEvent(activePixels, "Purchase", { ...payload, paymentMethod: 'creditCard' });
    // Disparar evento adicional 'creditCard'
    firePixelEvent(activePixels, "creditCard", payload);
};

/**
 * Dispara evento de Purchase para PIX
 */
export const firePixPurchase = (pixels: Pixel[], payload: EventPayload) => {
    const activePixels = pixels.filter(p => p.trackingOptions?.pix !== false);
    firePixelEvent(activePixels, "Purchase", { ...payload, paymentMethod: 'pix' });
    // Disparar evento adicional 'pix'
    firePixelEvent(activePixels, "pix", payload);
};

/**
 * Dispara evento de Boleto gerado (Purchase opcional)
 */
export const fireBoletoPurchase = (pixels: Pixel[], payload: EventPayload) => {
    const activePixels = pixels.filter(p => p.trackingOptions?.billet !== false);
    
    // Disparar evento 'boleto' sempre
    firePixelEvent(activePixels, "boleto", payload);
    
    // Disparar 'Purchase' apenas se habilitado
    const purchasePixels = activePixels.filter(p => p.enableBoletoEvent === true);
    if (purchasePixels.length > 0) {
        firePixelEvent(purchasePixels, "Purchase", { ...payload, paymentMethod: 'boleto' });
    }
};
